<script setup lang="ts">
import type { IFeedback } from "~/composables/feedback/useFeedback";

defineProps<{
  feedback: IFeedback;
}>();

const largeEmojis: Record<string, string> = {
  success: "/icons/emojis/check.svg",
  error: "/icons/emojis/loupe.svg",
  info: "/icons/emojis/gyrophare.svg",
};

const smallEmojis: Record<string, string> = {
  success: "/icons/emojis/check.svg",
  greeting: "/icons/emojis/fusée.svg",
  waiting: "/icons/emojis/sablier.svg",
};

function getEmoji(size: "small" | "large", type: string): string {
  if (size === "large") return largeEmojis[type];
  return smallEmojis[type] ?? "/icons/emojis/eyes.svg";
}
</script>

<template>
  <div v-if="feedback.size === 'large'" class="feedback_large cursor-pointer" :data-type="feedback.type">
    <img class="feedback_img_large" :src="getEmoji('large', feedback.type)" alt="" />
    <div>
      <h3 class="heading_3_regular">{{ feedback.title }}</h3>
      <h4 class="heading_4_regular">{{ feedback.message }}</h4>
      <button v-if="feedback.action" class="feedback_action_large" @click.stop="feedback.action.callback">
        {{ feedback.action.text }}
      </button>
    </div>
  </div>

  <div v-else class="feedback_small cursor-pointer" :class="{ 'greeting-shadow': feedback.type === 'greeting' }">
    <img class="feedback_img_small" :src="getEmoji('small', feedback.type)" alt="" />
    <div class="feedback_content_small">
      <h4 class="heading_4_regular feedback_text_small">{{ feedback.message }}</h4>
      <button v-if="feedback.action" class="feedback_action_small" @click.stop="feedback.action.callback">
        {{ feedback.action.text }}
      </button>
    </div>
  </div>
</template>

<style lang="scss">
.feedback_img_large {
  @apply w-[24px] h-[24px];
}

.feedback_large {
  @apply flex flex-row px-[24px] py-[12px] gap-[18px] rounded-[8px] items-center mb-2;
  background: var(--grey-light);
  border-left: 8px solid transparent;
  box-shadow: var(--shadow-medium);
  white-space: nowrap;

  &[data-type="success"] {
    border-left: 8px solid var(--green-main);
  }

  &[data-type="error"] {
    border-left: 8px solid var(--red-main);
  }

  &[data-type="info"] {
    border-left: 8px solid var(--primary-blue-color);
  }
}

.feedback_action_large {
  @apply underline mt-2 block bg-transparent border-none cursor-pointer;
  color: var(--primary-blue-color);
  padding: 0;
  text-align: left;
}

.feedback_img_small {
  @apply w-[16px] h-[16px];
}

.feedback_small {
  @apply flex flex-row px-[16px] py-[12px] gap-[8px] rounded-[8px] items-center mb-2;
  background: var(--primary-blue-color);
  box-shadow: var(--shadow-medium);
  white-space: pre-line;
}

.feedback_content_small {
  @apply flex gap-[4px] items-center;
}

.greeting-shadow {
  box-shadow: var(--shadow-medium);
}

.feedback_text_small {
  color: var(--white-main);
}

.feedback_action_small {
  @apply underline bg-transparent border-none cursor-pointer;
  color: var(--white-main);
  padding: 0;

  &:hover {
    @apply opacity-80;
  }
}
</style>
